import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    KeyRound, Laptop2, Download, CreditCard, Users as UsersIcon,
    ChevronRight, Loader2, Power, UserPlus, ShieldCheck, Building2,
    CreditCard as CardIcon, Ban, Clock3, Layers3, CalendarDays,
    FileText, Zap, CircleHelp, ClipboardList, BadgeCheck, MessageCircle,
    CircleCheck, Box, Monitor, Apple, Terminal, Receipt, ShieldAlert,
    Rocket, ListChecks, CircleDot,
} from "lucide-react";
import { http, errMsg, apiBaseUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_TONE = {
    active: "bg-success/10 text-success",
    trialing: "bg-sky-500/10 text-sky-600 dark:text-sky-300",
    past_due: "bg-warning/10 text-warning",
    suspended: "bg-destructive/10 text-destructive",
    canceled: "bg-muted text-muted-foreground",
    cancelled: "bg-muted text-muted-foreground",
};

function daysUntil(iso) {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

function fmtDate(iso) {
    if (!iso) return null;
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function TrialBanner({ status, endsAt }) {
    if (status !== "trialing") return null;
    const days = daysUntil(endsAt);
    return (
        <div className="mb-5 flex items-center gap-2 rounded-md border border-sky-500/40 bg-sky-500/[0.08] px-3 py-2 text-xs text-sky-600 dark:text-sky-300" data-testid="portal-overview-trial-banner">
            <Clock3 size={13} className="shrink-0" />
            {days == null ? "You're on a trial." : days === 0
                ? "Your trial ends today. Add a payment method to keep using MoovBytes."
                : `Your trial ends in ${days} day${days === 1 ? "" : "s"}. Add a payment method to keep using MoovBytes without interruption.`}
        </div>
    );
}

export default function PortalOverview() {
    const { user } = useAuth();
    const [license, setLicense] = useState(null);
    const [err, setErr] = useState("");
    const [events, setEvents] = useState(null);
    const [team, setTeam] = useState(null);
    const [invites, setInvites] = useState(null);
    const [desktop, setDesktop] = useState(null);
    const [devices, setDevices] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await http.get("/licensing/license");
                setLicense(data);
            } catch (e) {
                setErr(errMsg(e));
            }
        })();
        // Fetched once here and shared by RecentActivityCard (latest 5,
        // aggregated) and the Downloads metric card (30-day count) below,
        // rather than each issuing its own request for overlapping data.
        (async () => {
            try {
                const { data } = await http.get("/organization/activity", { params: { limit: 100 } });
                setEvents(data.events || []);
            } catch {
                setEvents([]);
            }
        })();
        (async () => {
            try {
                const { data } = await http.get("/users");
                setTeam(data || []);
            } catch {
                setTeam(null); // admin/auditor-only -- absent for other roles, card degrades gracefully
            }
        })();
        (async () => {
            try {
                const { data } = await http.get("/users/invites");
                setInvites((data || []).filter((i) => i.status === "pending"));
            } catch {
                setInvites(null); // admin-only
            }
        })();
        (async () => {
            try {
                const { data } = await http.get("/desktop-releases/assets");
                setDesktop(data);
            } catch { /* Latest Release / Get MoovBytes cards show their own empty state */ }
        })();
        (async () => {
            try {
                const { data } = await http.get("/licensing/devices", { params: { status: "all" } });
                setDevices(data.devices || []);
            } catch {
                setDevices([]);
            }
        })();
    }, []);

    const today = useMemo(
        () => new Date().toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" }),
        [],
    );

    if (err) {
        return <div className="p-6 text-sm text-destructive" data-testid="portal-overview-error">{err}</div>;
    }
    if (!license) {
        return (
            <div className="flex items-center justify-center p-16 text-muted-foreground" data-testid="portal-overview-loading">
                <Loader2 size={16} className="mr-2 animate-spin" /> Loading your account…
            </div>
        );
    }

    const seats = license.seats || {};

    return (
        <div className="p-6 sm:p-9" data-testid="portal-overview-page">
            <div className="mb-5 flex items-start justify-between gap-4">
                <div>
                    <div className="text-xs font-medium text-muted-foreground">Overview</div>
                    <h1 className="mt-1.5 font-display text-[26px] font-bold leading-tight tracking-tight sm:text-[28px]">
                        Welcome back, {license.org_name || user?.name}
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Manage your licenses, devices, downloads, and account settings.
                    </p>
                </div>
                <div className="hidden shrink-0 pt-1 text-xs font-medium text-muted-foreground sm:block">
                    {today}
                </div>
            </div>

            <TrialBanner status={license.status} endsAt={license.current_period_end} />

            <MetricGrid license={license} seats={seats} />
            <SecondaryMetricGrid license={license} user={user} team={team} invites={invites} events={events} />

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(300px,1.1fr)]">
                <div className="min-w-0 space-y-4">
                    <GetMoovBytesCard desktop={desktop} />
                    <RecentActivityCard events={events} />
                </div>
                <div className="space-y-4">
                    <QuickActionsCard />
                    <HelpCard />
                </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                <GettingStartedCard user={user} events={events} seats={seats} />
                <RecentDevicesCard devices={devices} />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                <OrganizationCard license={license} team={team} user={user} />
                <LatestReleaseCard desktop={desktop} />
            </div>

            <SystemStatusBar />
        </div>
    );
}

/* ─────────────────────────── Metric grid ─────────────────────────── */

function MetricCard({ icon: Icon, label, children, testId }) {
    return (
        <div
            className="flex min-h-[130px] gap-4 rounded-xl border border-border bg-surface p-[18px] shadow-[0_1px_2px_rgba(16,24,40,0.02)]"
            data-testid={testId}
        >
            <div className="flex h-[54px] w-[54px] shrink-0 items-center justify-center rounded-[14px] bg-brand/10 text-brand">
                <Icon size={24} />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-medium text-muted-foreground">{label}</div>
                {children}
            </div>
        </div>
    );
}

function MetricGrid({ license, seats }) {
    const pct = seats.max_devices ? Math.min(100, Math.round((seats.used / seats.max_devices) * 100)) : 0;
    const tone = STATUS_TONE[license.status] || "bg-muted text-muted-foreground";
    const days = daysUntil(license.current_period_end);

    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="portal-overview-metric-grid">
            <MetricCard icon={FileText} label="Your Plan" testId="portal-overview-metric-plan">
                <div className="mt-0.5 truncate font-display text-[25px] font-bold leading-tight">
                    {license.plan_label || "—"}
                </div>
                <div className="mt-1.5 flex items-center gap-2">
                    <span className={`inline-flex h-[21px] items-center rounded-full px-[9px] text-[11px] font-semibold capitalize ${tone}`}>
                        {(license.status || "—").replace("_", " ")}
                    </span>
                </div>
                <div className="mt-1.5 truncate text-xs text-muted-foreground">
                    {license.current_period_end ? `Renews on ${fmtDate(license.current_period_end)}` : "No renewal scheduled"}
                </div>
            </MetricCard>

            <MetricCard icon={Layers3} label="Total Licenses" testId="portal-overview-metric-licenses">
                <div className="mt-0.5 font-display text-[26px] font-bold leading-tight">
                    {seats.max_devices ?? "∞"}
                </div>
                <div className="mt-1.5 text-xs text-muted-foreground">Licenses purchased</div>
            </MetricCard>

            <MetricCard icon={Monitor} label="Activated Devices" testId="portal-overview-metric-devices">
                <div className="mt-0.5 font-display text-[26px] font-bold leading-tight tabular-nums">
                    {seats.used ?? 0} / {seats.max_devices ?? "∞"}
                </div>
                {seats.max_devices != null && (
                    <div className="mt-2 h-2 w-full max-w-[230px] overflow-hidden rounded-full bg-[hsl(var(--muted))]">
                        <div className="h-full rounded-full bg-brand transition-[width]" style={{ width: `${pct}%` }} />
                    </div>
                )}
                <div className="mt-1.5 text-xs text-muted-foreground">
                    {seats.available != null ? `${seats.available} activation${seats.available === 1 ? "" : "s"} available` : " "}
                </div>
            </MetricCard>

            <MetricCard icon={CalendarDays} label="Next Renewal" testId="portal-overview-metric-renewal">
                <div className="mt-0.5 font-display text-[24px] font-bold leading-tight">
                    {license.current_period_end ? fmtDate(license.current_period_end) : "No renewal scheduled"}
                </div>
                <div className="mt-1.5 text-xs text-muted-foreground">
                    {days != null ? `in ${days} day${days === 1 ? "" : "s"}` : " "}
                </div>
            </MetricCard>
        </div>
    );
}

/* ─────────────────────── Secondary metric row ────────────────────── */

const BILLING_META = {
    active: { label: "Current", sub: "No balance due", tone: "text-success bg-success/10" },
    trialing: { label: "Trial", sub: "No payment method yet", tone: "text-sky-600 bg-sky-500/10 dark:text-sky-300" },
    past_due: { label: "Past due", sub: "Action required", tone: "text-warning bg-warning/10" },
    suspended: { label: "Suspended", sub: "Contact support", tone: "text-destructive bg-destructive/10" },
    canceled: { label: "Canceled", sub: "No active subscription", tone: "text-muted-foreground bg-muted" },
    cancelled: { label: "Canceled", sub: "No active subscription", tone: "text-muted-foreground bg-muted" },
};

function SmallMetricCard({ icon: Icon, label, value, valueTone, sub, to, testId }) {
    const Wrapper = to ? Link : "div";
    const wrapperProps = to ? { to } : {};
    return (
        <Wrapper
            {...wrapperProps}
            data-testid={testId}
            className={
                "flex min-h-[104px] gap-3 rounded-[11px] border border-border bg-surface p-4 shadow-[0_1px_2px_rgba(16,24,40,0.02)]" +
                (to ? " transition-colors hover:border-brand/40" : "")
            }
        >
            <div className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-[11px] bg-brand/10 text-brand">
                <Icon size={19} />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-muted-foreground">{label}</div>
                <div className={"mt-0.5 truncate font-display text-[21px] font-bold leading-tight " + (valueTone || "")}>
                    {value}
                </div>
                <div className="mt-1 truncate text-[11px] text-muted-foreground">{sub || " "}</div>
            </div>
        </Wrapper>
    );
}

// Four account-management cards -- team, download activity, billing,
// security -- deliberately not migration telemetry, which belongs to
// MoovBytes Desktop, not this account portal.
function SecondaryMetricGrid({ license, user, team, invites, events }) {
    const pendingCount = invites?.length ?? null;
    const teamValue = team === null ? "—" : team.length;
    const teamSub =
        team === null
            ? "View team →"
            : pendingCount
                ? `${pendingCount} invitation${pendingCount === 1 ? "" : "s"} pending`
                : team.length <= 1
                    ? "You are the only member"
                    : `${team.length} members`;

    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const downloadCount = events === null
        ? null
        : events.filter((e) => e.action === "desktop_release.downloaded" && new Date(e.created_at).getTime() >= cutoff).length;

    const billing = BILLING_META[license.status] || { label: license.status || "—", sub: "", tone: "text-muted-foreground bg-muted" };

    const emailVerified = !!user?.email_verified;
    const mfaEnabled = !!user?.totp_enabled;
    let security;
    if (!emailVerified) {
        security = { label: "Action required", sub: "Email verification pending", tone: "text-warning", icon: ShieldAlert };
    } else if (!mfaEnabled) {
        security = { label: "Action needed", sub: "Enable MFA", tone: "text-warning", icon: ShieldAlert };
    } else {
        security = { label: "Protected", sub: "MFA enabled", tone: "text-success", icon: ShieldCheck };
    }

    return (
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="portal-overview-secondary-grid">
            <SmallMetricCard
                icon={UsersIcon}
                label="Team Members"
                value={teamValue}
                sub={teamSub}
                to="/portal/team"
                testId="portal-overview-metric-team"
            />
            <SmallMetricCard
                icon={Download}
                label="Downloads"
                value={downloadCount ?? "—"}
                sub="Desktop installs, last 30 days"
                to="/portal/downloads"
                testId="portal-overview-metric-downloads"
            />
            <SmallMetricCard
                icon={Receipt}
                label="Billing Status"
                value={billing.label}
                valueTone={billing.tone.split(" ")[0]}
                sub={billing.sub}
                to="/portal/billing"
                testId="portal-overview-metric-billing"
            />
            <SmallMetricCard
                icon={security.icon}
                label="Account Security"
                value={security.label}
                valueTone={security.tone}
                sub={security.sub}
                to="/portal/settings"
                testId="portal-overview-metric-security"
            />
        </div>
    );
}

/* ──────────────────── Getting Started / Recent Devices ───────────── */

function timeAgo(iso) {
    if (!iso) return null;
    const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return "just now";
    const m = Math.round(s / 60);
    if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
    const d = Math.round(h / 24);
    if (d === 1) return "yesterday";
    if (d < 30) return `${d} days ago`;
    return fmtDate(iso);
}

// Every step reflects real, independently-verifiable account state --
// none of these are ever marked complete speculatively.
function GettingStartedCard({ user, events, seats }) {
    const desktopDownloaded = events?.some((e) => e.action === "desktop_release.downloaded") ?? null;
    const steps = [
        { key: "created", label: "Create your MoovBytes account", done: true },
        { key: "verified", label: "Verify your email address", done: !!user?.email_verified, cta: "Verify", to: "/portal/settings" },
        { key: "downloaded", label: "Download MoovBytes Desktop", done: !!desktopDownloaded, cta: "Download", to: "/portal/downloads" },
        { key: "activated", label: "Activate your first device", done: (seats?.used ?? 0) > 0, cta: "Get started", to: "/portal/devices" },
    ];
    const doneCount = steps.filter((s) => s.done).length;
    const pct = Math.round((doneCount / steps.length) * 100);
    const allDone = doneCount === steps.length;

    return (
        <div className="flex min-h-[175px] flex-col rounded-xl border border-border bg-surface p-4 sm:p-[18px]" data-testid="portal-overview-getting-started">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                    <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
                        <ListChecks size={17} />
                    </div>
                    <div className="text-sm font-bold">Getting Started</div>
                </div>
                <div className="text-[11px] font-semibold text-muted-foreground">{doneCount} of {steps.length} complete</div>
            </div>

            {allDone ? (
                <div className="flex flex-1 items-center gap-2 py-3 text-sm text-success">
                    <CircleCheck size={16} /> You're all set up.
                </div>
            ) : (
                <div className="mt-2.5 space-y-1.5">
                    {steps.map((s) => (
                        <div key={s.key} className="flex items-center gap-2.5 py-0.5 text-xs">
                            {s.done ? (
                                <CircleCheck size={15} className="shrink-0 text-success" />
                            ) : s.cta ? (
                                <ShieldAlert size={15} className="shrink-0 text-warning" />
                            ) : (
                                <CircleDot size={15} className="shrink-0 text-muted-foreground" />
                            )}
                            <span className={"flex-1 " + (s.done ? "text-muted-foreground line-through" : "")}>{s.label}</span>
                            {!s.done && s.cta && (
                                <Link to={s.to} className="shrink-0 text-[11px] font-semibold text-brand hover:underline">
                                    {s.cta} →
                                </Link>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-auto pt-3">
                <div className="mb-1 flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>Account setup</span>
                    <span className="font-semibold">{pct}%</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-[hsl(var(--muted))]">
                    <div className="h-full rounded-full bg-brand transition-[width]" style={{ width: `${pct}%` }} />
                </div>
            </div>
        </div>
    );
}

const DEVICE_KIND_LABEL = {
    desktop_app: "MoovBytes Desktop",
    pc: "PC",
    server: "Server",
    nas: "NAS",
    other: "Other",
};

function RecentDevicesCard({ devices }) {
    const recent = useMemo(
        () => (devices || [])
            .slice()
            .sort((a, b) => new Date(b.last_seen_at || b.activated_at || 0) - new Date(a.last_seen_at || a.activated_at || 0))
            .slice(0, 3),
        [devices],
    );

    return (
        <div className="flex min-h-[175px] flex-col rounded-xl border border-border bg-surface p-4 sm:p-[18px]" data-testid="portal-overview-devices">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                    <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
                        <Monitor size={17} />
                    </div>
                    <div className="text-sm font-bold">Recent Devices</div>
                </div>
                <Link to="/portal/devices" className="text-[11.5px] font-semibold text-brand hover:underline">
                    View all →
                </Link>
            </div>

            {devices === null ? (
                <div className="flex flex-1 items-center gap-2 py-4 text-sm text-muted-foreground">
                    <Loader2 size={14} className="animate-spin" /> Loading…
                </div>
            ) : recent.length === 0 ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-2 py-3 text-center">
                    <Monitor size={28} className="text-muted-foreground/50" />
                    <div className="text-sm font-semibold">No devices activated yet</div>
                    <p className="max-w-[260px] text-[11.5px] text-muted-foreground">
                        Install MoovBytes Desktop and activate your first computer or server to see it here.
                    </p>
                    <Link
                        to="/portal/downloads"
                        className="mt-1 inline-flex h-8 items-center rounded-md bg-brand px-3 text-[11.5px] font-semibold text-white hover:bg-brand-dark"
                    >
                        Download MoovBytes
                    </Link>
                </div>
            ) : (
                <div className="mt-2 divide-y divide-border">
                    {recent.map((d) => (
                        <div key={d.id} className="flex items-center gap-3 py-2 text-xs">
                            <div className="min-w-0 flex-1">
                                <div className="truncate font-semibold">{d.device_name}</div>
                                <div className="truncate text-muted-foreground">
                                    {DEVICE_KIND_LABEL[d.kind] || d.kind}
                                    {d.last_seen_at ? ` · Last seen ${timeAgo(d.last_seen_at)}` : ""}
                                </div>
                            </div>
                            <span className={
                                "inline-flex shrink-0 items-center gap-1 text-[11px] font-medium " +
                                (d.status === "active" ? "text-success" : "text-muted-foreground")
                            }>
                                <span className={"h-1.5 w-1.5 rounded-full " + (d.status === "active" ? "bg-success" : "bg-muted-foreground")} />
                                {d.status === "active" ? "Active" : "Deactivated"}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ───────────────────── Organization / Latest release ─────────────── */

function OrganizationCard({ license, team, user }) {
    const adminCount = team ? team.filter((t) => t.role === "admin" || t.role === "superadmin").length : null;
    return (
        <div className="rounded-xl border border-border bg-surface p-[18px]" data-testid="portal-overview-organization">
            <div className="flex items-center gap-3">
                <div className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-[11px] bg-brand/10 text-brand">
                    <Building2 size={19} />
                </div>
                <div className="text-xs font-medium text-muted-foreground">Organization</div>
            </div>
            <div className="mt-2 font-display text-2xl font-bold">{license.org_name || user?.name || "—"}</div>
            <div className="mt-1.5 text-xs text-muted-foreground">
                {license.plan_label ? `${license.plan_label} account` : "—"}
                {team ? ` · ${team.length} member${team.length === 1 ? "" : "s"}` : ""}
                {adminCount ? ` · ${adminCount} administrator${adminCount === 1 ? "" : "s"}` : ""}
            </div>
            <Link
                to="/portal/settings"
                className="mt-3 inline-block text-[12.5px] font-semibold text-brand hover:underline"
            >
                Manage organization →
            </Link>
        </div>
    );
}

function LatestReleaseCard({ desktop }) {
    const windowsAsset = desktop?.platforms?.windows;
    return (
        <div className="rounded-xl border border-border bg-surface p-[18px]" data-testid="portal-overview-latest-release">
            <div className="flex items-center gap-3">
                <div className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-[11px] bg-brand/10 text-brand">
                    <Rocket size={19} />
                </div>
                <div className="text-xs font-medium text-muted-foreground">Latest Release</div>
            </div>
            {desktop?.version ? (
                <>
                    <div className="mt-2 font-display text-2xl font-bold">MoovBytes Desktop v{desktop.version}</div>
                    <div className="mt-1.5 text-xs text-muted-foreground">
                        {desktop.channel ? `${desktop.channel[0].toUpperCase()}${desktop.channel.slice(1)}` : "Stable"}
                        {desktop.released_at ? ` · Released ${fmtDate(desktop.released_at)}` : ""}
                        {windowsAsset?.available ? " · Windows, macOS & Linux" : ""}
                    </div>
                </>
            ) : (
                <div className="mt-2 text-sm text-muted-foreground">No MoovBytes Desktop build published yet.</div>
            )}
            <Link
                to="/portal/downloads"
                className="mt-3 inline-block text-[12.5px] font-semibold text-brand hover:underline"
            >
                View release notes →
            </Link>
        </div>
    );
}

/* ───────────────────────── Get MoovBytes ─────────────────────────── */

// Only real, currently-published products. A "Storage Gateway" download
// was requested for parity with a reference design, but no such release
// type exists on the backend (routers/desktop_releases.py and
// routers/agent_updates.py cover Desktop + Agent only) -- showing a third
// card for it would be a dead/fake download, which the redesign brief
// itself says not to do.
function GetMoovBytesCard({ desktop }) {
    const [agent, setAgent] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await http.get("/v1/agents/assets");
                setAgent(data);
            } catch { /* card below shows its own not-built state */ }
        })();
    }, []);

    return (
        <div className="rounded-xl border border-border bg-surface p-4 sm:p-[18px]" data-testid="portal-overview-downloads">
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3.5">
                    <div className="flex h-[54px] w-[54px] shrink-0 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                        <Download size={25} />
                    </div>
                    <div>
                        <div className="font-display text-[19px] font-bold leading-tight">Get MoovBytes</div>
                        <div className="mt-0.5 text-[13.5px] text-muted-foreground">
                            Download the latest version of MoovBytes and start migrating.
                        </div>
                    </div>
                </div>
                <Link
                    to="/portal/downloads"
                    data-testid="portal-overview-view-all-downloads"
                    className="shrink-0 whitespace-nowrap text-[12.5px] font-semibold text-brand hover:underline"
                >
                    View all downloads →
                </Link>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                <ProductCard
                    icon={Monitor}
                    title="MoovBytes Desktop"
                    release={desktop && { version: desktop.version, released_at: desktop.released_at }}
                    primary
                    assets={desktop?.platforms}
                    buttonLabel="Download Installer"
                    testId="portal-overview-download-desktop"
                />
                <ProductCard
                    icon={Box}
                    title="MoovBytes Agent"
                    release={agent && { version: agent.version, released_at: null }}
                    assets={agent?.assets}
                    buttonLabel="Download Agent"
                    testId="portal-overview-download-agent"
                />
            </div>
        </div>
    );
}

// Only the 3 primary OS slots -- the Agent catalog also has linux-deb/
// linux-rpm package variants, but those are secondary formats for the
// full Downloads page, not this compact summary card.
const OS_ORDER = [
    { key: "windows", label: "Windows", icon: Monitor },
    { key: "macos", label: "macOS", icon: Apple },
    { key: "linux", label: "Linux", icon: Terminal },
];

function detectOS() {
    const ua = (navigator.userAgent || "") + (navigator.platform || "");
    if (/mac/i.test(ua)) return "macos";
    if (/linux/i.test(ua) && !/android/i.test(ua)) return "linux";
    return "windows";
}

function ProductCard({ icon: Icon, title, release, assets, primary, buttonLabel, testId }) {
    const available = OS_ORDER.filter((os) => assets?.[os.key]?.available);
    const detected = detectOS();
    const chosen = available.find((os) => os.key === detected) || available[0];
    const asset = chosen ? assets[chosen.key] : null;
    const url = asset?.download_url ? `${apiBaseUrl()}${asset.download_url}` : null;

    return (
        <div className="flex min-h-[168px] flex-col rounded-[10px] border border-border bg-surface p-4" data-testid={testId}>
            <Icon size={34} className="text-brand" />
            <div className="mt-2.5 text-sm font-bold">{title}</div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs font-medium text-muted-foreground">
                {available.length > 0 ? (
                    available.map((os) => (
                        <span key={os.key} className="inline-flex items-center gap-1">
                            <os.icon size={11} /> {os.label}
                        </span>
                    ))
                ) : (
                    "Windows / macOS / Linux"
                )}
            </div>
            <div className="mt-0.5 text-[11.5px] text-muted-foreground">
                {release?.version
                    ? `v${release.version}${release.released_at ? ` | ${fmtDate(release.released_at)}` : ""}`
                    : " "}
            </div>
            <div className="mt-auto pt-3">
                {url ? (
                    <a
                        href={url}
                        aria-label={`${buttonLabel} for ${chosen.label}`}
                        className={
                            "flex h-[38px] items-center justify-center gap-[7px] rounded-md text-xs font-semibold transition-colors " +
                            (primary
                                ? "bg-brand text-white hover:bg-brand-dark"
                                : "border border-brand text-brand hover:bg-brand/5")
                        }
                    >
                        <Download size={15} /> {buttonLabel} for {chosen.label}
                    </a>
                ) : (
                    <div className="flex h-[38px] items-center justify-center rounded-md border border-dashed border-border text-[11px] text-muted-foreground">
                        Not built yet
                    </div>
                )}
                <Link
                    to="/portal/downloads"
                    className="mt-2.5 block text-center text-[11.5px] font-medium text-brand hover:underline"
                >
                    {available.length > 1 ? "Other platforms & release notes →" : "Release notes →"}
                </Link>
            </div>
        </div>
    );
}

/* ───────────────────────── Quick actions ─────────────────────────── */

const QUICK_ACTIONS = [
    { to: "/portal/devices", icon: Laptop2, label: "Manage devices" },
    { to: "/portal/licenses", icon: KeyRound, label: "Manage licenses" },
    { to: "/portal/billing", icon: CreditCard, label: "Manage billing" },
    { to: "/portal/team", icon: UsersIcon, label: "Invite your team" },
    { to: "/portal/licenses", icon: BadgeCheck, label: "View license details" },
];

function QuickActionsCard() {
    return (
        <div className="rounded-xl border border-border bg-surface py-3.5" data-testid="portal-overview-quick-actions">
            <div className="flex items-center gap-3 px-[18px] pb-2.5">
                <Zap size={21} className="text-brand" />
                <div className="font-display text-base font-bold">Quick Actions</div>
            </div>
            <div>
                {QUICK_ACTIONS.map((a, i) => (
                    <Link
                        key={a.label}
                        to={a.to}
                        data-testid={`portal-overview-quicklink-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={
                            "flex h-9 items-center gap-2.5 px-[18px] text-xs font-medium transition-colors hover:bg-muted" +
                            (i > 0 ? " border-t border-border" : "")
                        }
                    >
                        <a.icon size={15} className="text-muted-foreground" />
                        <span className="flex-1">{a.label}</span>
                        <ChevronRight size={14} className="text-muted-foreground" />
                    </Link>
                ))}
            </div>
        </div>
    );
}

/* ────────────────────────────── Help ──────────────────────────────── */

const HELP_LINKS = [
    { to: "/docs", icon: FileText, label: "Documentation" },
    { to: "/docs", icon: ClipboardList, label: "Installation Guide" },
    { to: "/docs", icon: BadgeCheck, label: "Licensing Guide" },
    { to: "/portal/support", icon: MessageCircle, label: "Contact Support" },
];

function HelpCard() {
    return (
        <div className="rounded-xl border border-border bg-surface py-3.5" data-testid="portal-overview-help">
            <div className="flex items-center gap-3 px-[18px] pb-2.5">
                <div className="flex h-[34px] w-[34px] items-center justify-center rounded-full bg-brand/10 text-brand">
                    <CircleHelp size={18} />
                </div>
                <div className="font-display text-base font-bold">Need Help?</div>
            </div>
            <div>
                {HELP_LINKS.map((l, i) => (
                    <Link
                        key={l.label}
                        to={l.to}
                        data-testid={`portal-overview-help-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={
                            "flex h-9 items-center gap-2.5 px-[18px] text-xs font-medium transition-colors hover:bg-muted" +
                            (i > 0 ? " border-t border-border" : "")
                        }
                    >
                        <l.icon size={15} className="text-muted-foreground" />
                        <span className="flex-1">{l.label}</span>
                        <ChevronRight size={14} className="text-muted-foreground" />
                    </Link>
                ))}
            </div>
        </div>
    );
}

/* ─────────────────────────── System status ───────────────────────── */

// No per-service health endpoint exists on the backend today (only a bare
// GET /api/ping liveness check) -- so these three columns all reflect that
// one real signal rather than independently-monitored services. Flagged
// as a known limitation; do not read this as three separate health checks.
// Full-width by design -- the single source of truth for platform status
// on this page (there is exactly one System Status component; it used to
// also live as a small card in the right column, which duplicated this).
function SystemStatusBar() {
    const [ok, setOk] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                await http.get("/ping");
                setOk(true);
            } catch {
                setOk(false);
            }
        })();
    }, []);

    const rows = ["Licensing Service", "Download Service", "Billing Service"];

    return (
        <div className="mt-4 rounded-xl border border-border bg-surface p-[18px]" data-testid="portal-overview-status">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <CircleCheck size={17} className={ok === false ? "text-destructive" : "text-success"} />
                    <div className="text-[15px] font-bold">System Status</div>
                </div>
                <a
                    href="https://status.moovbytes.com"
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-semibold text-brand hover:underline"
                >
                    View status →
                </a>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
                {ok === null
                    ? "Checking MoovBytes services…"
                    : ok
                        ? "All MoovBytes services are operating normally."
                        : "We're having trouble reaching MoovBytes services right now."}
            </p>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {rows.map((label) => (
                    <div key={label} className="rounded-lg border border-border bg-background/40 px-4 py-3">
                        <div className="text-xs text-muted-foreground">{label}</div>
                        <div className={"mt-1 flex items-center gap-1.5 text-sm font-semibold " + (ok === false ? "text-destructive" : "text-success")}>
                            <span className={"h-2 w-2 rounded-full " + (ok === false ? "bg-destructive" : "bg-success")} />
                            {ok === null ? "Checking…" : ok ? "Operational" : "Unavailable"}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ───────────────────────── Recent activity ───────────────────────── */

// Maps GET /organization/activity's curated action list (organization.py's
// _ACTIVITY_ACTIONS -- only real, currently-emitted audit actions, never
// invented event types) to the label/icon shown here.
const ACTIVITY_META = {
    "license.device.activated": { label: "Device activated", icon: Laptop2, tone: "text-success bg-success/10" },
    "license.device.deactivated": { label: "Device deactivated", icon: Power, tone: "text-muted-foreground bg-muted" },
    "superadmin.license.device.revoked": { label: "Device activation revoked", icon: Ban, tone: "text-destructive bg-destructive/10" },
    "license.subscription.updated": { label: "Subscription updated", icon: CardIcon, tone: "text-warning bg-warning/10" },
    "organization.updated": { label: "Organization details changed", icon: Building2, tone: "text-brand bg-brand/10" },
    "users.invited": { label: "Team member invited", icon: UserPlus, tone: "text-success bg-success/10" },
    "users.invite_cancelled": { label: "Invitation cancelled", icon: UserPlus, tone: "text-muted-foreground bg-muted" },
    "users.role_changed": { label: "Role changed", icon: ShieldCheck, tone: "text-brand bg-brand/10" },
    "desktop_release.downloaded": { label: "MoovBytes Desktop downloaded", icon: Download, tone: "text-brand bg-brand/10" },
};

const OVERVIEW_ROW_LIMIT = 5;

// Collapses runs of the same download-type event into one aggregated row
// ("MoovBytes Desktop downloaded — 3 downloads today") rather than letting
// repeated downloads crowd out every other kind of activity on a
// summary page. Only aggregates real consecutive duplicates -- never
// invents a count when the backend hasn't actually recorded that many.
function aggregateEvents(events) {
    const out = [];
    for (const e of events) {
        const last = out[out.length - 1];
        if (last && last.__action === e.action && ACTIVITY_META[e.action]?.label.includes("downloaded")) {
            last.__count += 1;
            last.__latest = e;
        } else {
            out.push({ __action: e.action, __count: 1, __latest: e, __first: e });
        }
        if (out.length >= OVERVIEW_ROW_LIMIT) break;
    }
    return out;
}

function RecentActivityCard({ events }) {
    const rows = useMemo(() => (events ? aggregateEvents(events) : []), [events]);

    return (
        <div className="rounded-xl border border-border bg-surface" data-testid="portal-overview-activity">
            <div className="flex h-[54px] items-center justify-between px-[18px]">
                <div className="flex items-center gap-2.5">
                    <div className="flex h-[34px] w-[34px] items-center justify-center rounded-full bg-brand/10 text-brand">
                        <Clock3 size={17} />
                    </div>
                    <div className="font-display text-base font-bold">Recent Account Activity</div>
                </div>
                <Link to="/audit" className="text-xs font-semibold text-brand hover:underline">
                    View all activity →
                </Link>
            </div>

            {events === null && (
                <div className="flex items-center gap-2 px-[18px] pb-4 text-sm text-muted-foreground">
                    <Loader2 size={14} className="animate-spin" /> Loading…
                </div>
            )}
            {events?.length === 0 && (
                <div className="px-[18px] pb-4 text-sm text-muted-foreground">
                    No account activity yet.
                </div>
            )}
            {rows.length > 0 && (
                <table className="w-full border-t border-border text-left text-xs" data-testid="portal-overview-activity-table">
                    <thead>
                        <tr className="text-[10.5px] uppercase tracking-[0.03em] text-muted-foreground">
                            <th className="w-[34%] px-[18px] py-2 font-semibold">Event</th>
                            <th className="w-[31%] px-2 py-2 font-semibold">Details</th>
                            <th className="w-[17%] px-2 py-2 font-semibold">User</th>
                            <th className="w-[18%] px-[18px] py-2 font-semibold">Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const e = row.__latest;
                            const meta = ACTIVITY_META[e.action] || { label: e.action, icon: Clock3, tone: "text-muted-foreground bg-muted" };
                            const Icon = meta.icon;
                            return (
                                <tr key={`${e.action}-${e.created_at}`} className="border-t border-border">
                                    <td className="px-[18px] py-2">
                                        <div className="flex items-center gap-2.5">
                                            <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${meta.tone}`}>
                                                <Icon size={14} />
                                            </div>
                                            <span className="truncate">
                                                {meta.label}
                                                {row.__count > 1 && (
                                                    <span className="ml-1.5 text-muted-foreground">· {row.__count} downloads</span>
                                                )}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="truncate px-2 py-2 text-muted-foreground">{_eventDetail(e) || "—"}</td>
                                    <td className="truncate px-2 py-2 text-muted-foreground">{e.user_email || e.actor_email || "—"}</td>
                                    <td className="px-[18px] py-2 text-muted-foreground">{fmtDate(e.created_at) || "—"}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function _eventDetail(e) {
    const d = e.metadata || {};
    if (d.device_name) return d.device_name;
    if (d.email) return d.email + (d.role ? ` · ${d.role}` : "");
    if (d.new_role) return `→ ${d.new_role}`;
    if (d.fields) return Array.isArray(d.fields) ? d.fields.join(", ") : null;
    return null;
}
